import React from 'react';
import { getLevelLabels } from '../../config/siteConfig';

export const SCENE_SHORTCUTS = [
  { key: 'Esc', label: 'Exit section and return to Pan' },
  { key: 'P', label: 'Pan' },
  { key: 'I', label: 'Info — click object, opens Info panel' },
  { key: 'D', label: 'Distance' },
  { key: 'Z', label: 'Zoom to click' },
  { key: '3×', label: 'Triple-click a point to zoom' },
  { key: 'C', label: 'Colour' },
  { key: 'H', label: 'Hide layer (click object)' },
  { key: 'U', label: 'Undo last hide' },
  { key: 'E', label: 'Zoom to extent' },
  { key: 'N', label: 'Plan view — look down, north up' },
  { key: 'X', label: 'Section tool' },
  { key: '[ ]', label: 'Step section back / forward' },
  { key: 'L', label: 'Layers / Info / Stats panel' },
  { key: 'B', label: 'Labels' },
  { key: 'S', label: 'Screenshot' },
  { key: '/', label: 'Find layer and fly to it' },
  { key: 'G', label: 'Toggle plan grid' },
  { key: '?', label: 'This help' },
];

export default function HelpOverlay({ open, onClose, projectName }) {
  if (!open) return null;

  const shortcuts = getLevelLabels()?.levels?.length
    ? SCENE_SHORTCUTS
    : SCENE_SHORTCUTS.filter((row) => row.key !== 'B');

  return (
    <div className="fixed inset-0 z-[160] bg-black/70 flex items-center justify-center p-4 backdrop-blur-[2px]">
      <div
        role="dialog"
        aria-label="Keyboard shortcuts"
        className="w-full max-w-md max-h-[min(90dvh,40rem)] overflow-y-auto rounded-md border border-yellow-600/40 bg-zinc-950 shadow-[0_20px_60px_rgba(0,0,0,0.6)] p-5"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <p className="text-[9px] uppercase tracking-[0.16em] text-yellow-500/80">Help</p>
            <h3 className="text-sm font-semibold text-yellow-400 tracking-wide">Shortcuts</h3>
            {projectName ? (
              <p className="text-[11px] text-zinc-500 mt-0.5">{projectName}</p>
            ) : null}
          </div>
          <button
            type="button"
            className="m-0 h-7 px-2 text-[10px] uppercase tracking-wider text-zinc-400 border border-zinc-700 rounded hover:border-yellow-500/50 hover:text-yellow-400"
            onClick={onClose}
          >
            Close
          </button>
        </div>
        <ul className="divide-y divide-zinc-800/80">
          {shortcuts.map((row) => (
            <li key={row.key} className="flex items-center justify-between gap-3 py-1.5">
              <span className="text-xs text-zinc-300">{row.label}</span>
              <kbd className="min-w-[1.5rem] text-center px-1.5 py-0.5 rounded-sm border border-yellow-600/40 bg-black text-[10px] font-semibold text-yellow-400">
                {row.key}
              </kbd>
            </li>
          ))}
        </ul>
        <div className="mt-3 space-y-2 text-[10px] text-zinc-500 leading-relaxed">
          <p className="mdv-help-mobile">
            On a phone, use the bar at the bottom: Layers, Tools, Draw, Find, Views, and More.
            Only one panel opens at a time so the 3D view stays readable. Drag with one finger to
            orbit, pinch to zoom, two fingers to pan.
          </p>
          <p>
            Reload restores camera, visible layers, opacity, grid, lighting look, and the Layers panel (open, dock, tab,
            folders, scroll). Dock the panel to the left or right of the 3D view, or drag it to a screen edge; collapse
            it to a slim rail.
          </p>
          <p>
            Use Preferences → Studio to switch lighting: Realistic (Studio, Daylight, Overcast, Mine
            lamps), Game (Bright pop, Neon rim, Cinematic), or Technical (High vis, Classic). Pair with Background for
            the full setup. Scene data shows live FPS, GPU, memory, and per-model cost as you open layers.
          </p>
          <p>
            Layers tabs: Browse, Visible, Cached, and Recent sit together as compact lists. Holes (query + editable
            legend) and BlockModels (global grade filter and editable colour legend) are the two tools below. Use Reveal to jump
            from a flat list back into the folder tree. Iso can isolate several layers — click Iso on each,
            click again to remove one, and the last Iso restores the previous view. The slider fades GLB opacity.
          </p>
          <p>
            Click a blast block or drive with Info (I) to open attributes, sampling, and production in the Info tab.
            Views are numbered — Share copies a <code className="text-yellow-500/80">?view=</code> link.
            Section (X) clips a corridor; Plot 2D projects holes onto the plane and exports PDF.
            N is plan-north-up. Distance snaps to collars. Import CSV/DXF strings from Tools.
            Esc exits the section and returns to Pan. Click an active tool again to return to Pan.
          </p>
        </div>
      </div>
    </div>
  );
}
